/**
 * Where to put a panel that is portalled to <body> but belongs to a control
 * somewhere in the page.
 *
 * The Theme Builder's rail is an `overflow: auto` column. An absolutely
 * positioned panel inside one is CLIPPED by it, which slices a picker in half
 * for every control in the lower part of the rail -- so the panels are
 * portalled out and positioned by hand against the trigger's viewport rect.
 *
 * Shared rather than written twice: the colour picker and the help tips both
 * do this, and two copies of a flip-and-clamp calculation is exactly the sort
 * of thing that drifts until one of them is subtly wrong near an edge.
 *
 * @param {Element} el      the trigger
 * @param {number} width    the panel's width in px
 * @param {number} height   the panel's height in px, for the flip decision
 * @param {number} [gap]    space between trigger and panel
 * @returns {{left: number, top: number, flipped: boolean}|null}
 */
export function anchorTo (el, width, height, gap) {
    if (!el || typeof el.getBoundingClientRect !== 'function') return null;

    const rect = el.getBoundingClientRect();
    const offset = typeof gap === 'number' ? gap : 6;
    const margin = 8;

    const below = window.innerHeight - rect.bottom;
    // Flip above only when there is not room below AND there is more room
    // above. Flipping into an even smaller space just moves the problem.
    const flipped = below < height + offset && rect.top > below;

    // `left` is clamped to the viewport so a control near the right edge does
    // not open a panel half off-screen.
    return {
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
        top: flipped
            ? Math.max(margin, rect.top - height - offset)
            : rect.bottom + offset,
        flipped
    };
}

/**
 * Wire up the listeners every portalled panel needs: dismiss on an outside
 * click, dismiss on Escape, and re-place on scroll or resize.
 *
 * Re-place rather than dismiss on scroll: the rail scrolls under these
 * panels, and closing on scroll would fight a drag on the colour picker's own
 * saturation square.
 *
 * @param {object} options
 * @param {Function} options.onDismiss
 * @param {Function} options.onReplace
 * @param {Array<{current: Element}>} options.ignore  refs an outside click may land in
 * @returns {Function} teardown
 */
export function bindPopover ({ onDismiss, onReplace, ignore }) {
    function onDown (ev) {
        const inside = (ignore || []).some(ref => (
            ref && ref.current && ref.current.contains(ev.target)
        ));
        if (!inside) onDismiss();
    }
    function onKey (ev) {
        if (ev.key === 'Escape') onDismiss();
    }

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReplace);
    // Capture phase: the rail is the element that scrolls, not the window, and
    // a scroll event on it does not bubble.
    window.addEventListener('scroll', onReplace, true);

    return () => {
        document.removeEventListener('mousedown', onDown);
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', onReplace);
        window.removeEventListener('scroll', onReplace, true);
    };
}
